import * as React from "react";
import { Header } from "@gc/global-components";
import { LocalizedComponentProps } from "@shared-ui/localization-context";
import { AnalyticsStore, Controller, logger, PageStore } from "bernie-core";
import { Logger } from "bernie-logger";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { FlexFooter } from "components/footer/FlexFooter";
import { TracingInfo } from "components/footer/TracingInfo";
import RedirectBanner from "components/redirectBanner/RedirectBannerWrapper";
import { VirtualAgentControl } from "@shared-ui/customer-virtual-agent-control";
import { AdScripts } from "components/utility/Ads/AdScripts";
import {
  doGlassbox,
  sendImmediateTrackEvent,
  Action,
  FlexTrackingInfo,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { getQueryParamValue } from "components/utility/FeatureFlag/UrlUtils";
import { Notification } from "components/utility/Notification/Notification";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { MISSING_CONTEXT_AND_FLEX_VIEWMODEL } from "config/systemEvents";
import { inject } from "mobx-react";
import { QualtricsScript } from "components/utility/Qualtrics/QualtricsScript";
import { AdvertisingStore } from "stores/AdvertisingStore";
import { CompositionStore } from "src/stores/CompositionStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { ExperimentContext, ExtendedContextStore, TrackingContext } from "typings/flexFramework/FlexDefinitions";
import { AlterLocalizationProvider } from "bernie-alter";
import { ScrollTracker } from "src/components/utility/analytics/ScrollTracker/ScrollTracker";
import { MABMicroMessage, getMABMicroMessage, MABPlatform } from "src/components/utility/analytics/MABAnalyticsUtils";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
  PageTypesForBackground,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext";
import { ClientChatbotStore } from "stores/chatbotStore/ClientChatbotStore";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { BlossomComponentTreeContext } from "components/flexFramework/domain/BlossomComponentTreeContext";

export interface LandingPageProps extends LocalizedComponentProps {
  advertising?: AdvertisingStore;
  analytics?: AnalyticsStore;
  flexViewModel?: FlexViewModelStore;
  context?: ExtendedContextStore;
  compositionStore?: CompositionStore;
  flexModuleModelStore?: FlexModuleModelStore;
  page?: PageStore;
  chatbot?: ClientChatbotStore;
  route?: Controller;
}

interface LandingPageStates {
  redirectBanner: React.ReactElement | null;
}

@logger("LandingPage")
@inject(
  "advertising",
  "analytics",
  "flexViewModel",
  "context",
  "compositionStore",
  "page",
  "flexModuleModelStore",
  "chatbot"
)
export default class LandingPage extends React.Component<LandingPageProps & { logger?: Logger }, LandingPageStates> {
  constructor(props: LandingPageProps & { logger?: Logger }) {
    super(props);
    this.shouldDisplayChatbot = this.shouldDisplayChatbot.bind(this);
  }
  public componentDidMount() {
    const { compositionStore, context, analytics, chatbot } = this.props;
    document.title = compositionStore ? compositionStore.title : "";

    // Record user's interactions with the page if enabled
    if (this.shouldPerformGlassboxAnalytics(context?.experimentContext)) {
      doGlassbox();
    }

    if (analytics) {
      analytics.markPageUsable();
    }

    if (this.shouldDisplayChatbot()) {
      chatbot!.injectChatbotScriptAndInitializeOnLoad(document, analytics!, this.props.logger!);
    }

    const banditContext = compositionStore && compositionStore.composition ? compositionStore.composition.bandit : null;
    // send the bandit event if exists
    if (banditContext && context && context.deviceInformation) {
      const {
        deviceInformation,
        site: { domain: pos },
      } = context;

      const trackingInfo: FlexTrackingInfo = {
        moduleName: "LandingPage",
        rfrr: "homepage_bandit",
        action: Action.IMPRESSION,
      };

      const { mobile, tablet } = deviceInformation;
      const mabTrackingInfo: MABMicroMessage = getMABMicroMessage(
        banditContext,
        "homepage",
        "moduleOrderingBanditLoad",
        mobile ? MABPlatform.Mobile : tablet ? MABPlatform.Tablet : MABPlatform.Desktop,
        pos
      );

      sendImmediateTrackEvent(trackingInfo, analytics, mabTrackingInfo);
    }
    new ScrollTracker(analytics).track();
  }

  private shouldPerformGlassboxAnalytics(context?: ExperimentContext): boolean {
    if (!context) {
      return false;
    }

    const forced = getQueryParamValue("forceGlass") === "true";
    const isStorefrontExperimentEnabled = context.experiments["Flex_Blossom_Glassbox_Throttle"] === 1;
    const isHTGGlassboxEnabled = context.experiments["Flex_HTG_Blossom_Glass_Throttle"] === 1;

    return isStorefrontExperimentEnabled || isHTGGlassboxEnabled || forced;
  }

  // Prevent the page from being rendered twice
  public shouldComponentUpdate(nextProps: any, nextState: any) {
    return false;
  }

  private shouldDisplayChatbot(): boolean {
    const { chatbot, analytics } = this.props;

    return !!(
      chatbot?.chatbotUseVacPath &&
      analytics &&
      logger &&
      chatbot?.locale &&
      chatbot?.injectChatbotScriptAndInitializeOnLoad
    );
  }

  private addDxPageProps = (): { itemScope: boolean; itemType: string } | null => {
    const { context } = this.props;
    const pageType = context && context.searchContext && context.searchContext.pageType;

    if (pageType !== "Destination-Discovery") {
      return null;
    }

    return {
      itemScope: true,
      itemType: "http://schema.org/LocalBusiness",
    };
  };

  private getBackgroundColor(context: ExtendedContextStore): BackgroundColorTokens {
    if (context.site.brand === "expedia") {
      for (const key in PageTypesForBackground) {
        if (PageTypesForBackground.hasOwnProperty(key)) {
          const pageId: string = PageTypesForBackground[key];
          if (context.pageId === pageId) {
            return BackgroundColorTokens.BACKGROUND_WHITE;
          }
        }
      }
    }

    return BackgroundColorTokens.BACKGROUND_DEFAULT;
  }

  public render() {
    const { context, flexViewModel, flexModuleModelStore, compositionStore, chatbot, analytics } = this.props;

    if (!context || !flexViewModel || !compositionStore || !flexModuleModelStore) {
      this.props.logger!.logEvent(
        MISSING_CONTEXT_AND_FLEX_VIEWMODEL,
        `isMissingContext=${!context} isMissingFlexViewModel=${!flexViewModel} isMissingCompositionStore=${!compositionStore}`
      );

      return null;
    }

    const trackingContext: TrackingContext = flexViewModel && flexViewModel.trackingContext;
    const blossomComponentTree = compositionStore.hydrateBlossomComponentTree(flexModuleModelStore, context);
    const flexComponents = compositionStore.composition?.page.regionList;
    const backgroundColorToken = this.getBackgroundColor(context);

    if (this.props.advertising && context.queryParams) {
      this.props.advertising.overrideWithQueryParams(context.queryParams);
    }

    const isCanary = context.queryParams && context.queryParams["blossomCanary"] === "1";

    const shouldShowChatbot = !!(chatbot?.chatbotUseVacPath && chatbot?.locale && analytics);

    return (
      <AlterLocalizationProvider>
        <PageBackgroundContext.Provider value={backgroundColorToken}>
          <RedirectBanner context={context} />
          <div className={`uitk-grid ${backgroundColorToken}`}>
            <Header showLobLinks={true} />
            <div
              className={`uitk-cell ${context.searchContext && context.searchContext.pageType}`}
              {...this.addDxPageProps()}
            >
              <BlossomComponentTreeContext.Provider value={blossomComponentTree}>
                <BlossomComponents flexComponents={flexComponents} context={context} />
              </BlossomComponentTreeContext.Provider>

              {shouldShowChatbot && (
                <>
                  {/*The span here is only for testing purposes since
                     we cannot test the presence of the shared ui component*/}
                  <span data-testid="landing-page-chatbot" />
                  <VirtualAgentControl
                    data-testid="landing-page-chatbot"
                    inputs={{}}
                    applicationName="blossom-flex-ui"
                    pageName={trackingContext.pageName}
                    clientOverrides={{
                      subscribedEvents: chatbot!.subscribedEvents,
                      eventHandler: (event) => chatbot!.eventHandler(analytics!, event),
                    }}
                  />
                </>
              )}

              <Viewport>
                <ViewSmall>
                  <FlexFooter footer={flexViewModel.footer} context={context} isMobile={true} />
                </ViewSmall>
                <ViewMedium>
                  <FlexFooter footer={flexViewModel.footer} context={context} isMobile={false} />
                </ViewMedium>
              </Viewport>
            </div>
            <Notification />
          </div>
          <TracingInfo extendedContext={context} />
          <AdScripts pageName={trackingContext.pageName} context={context} />
          <SchemaOrgScript structuredData={flexViewModel.structuredData} />
          {!isCanary && <QualtricsScript context={context} pageName={trackingContext.pageName} />}
        </PageBackgroundContext.Provider>
      </AlterLocalizationProvider>
    );
  }
}

export { LandingPage };
